import { Box, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import React, { useState } from 'react';
import { TEAM_BENEFITS } from 'shared/constants/subscriptions/plans';

import purpleWave from '@/assets/animations/purple-wave.json';
import { EquinetTeams, RightArrowIcon } from '@/assets/svg';
import { Button } from '@/components';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useTrialEligibility } from '@/hooks/useTrialEligibility';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { UnlockPremiumModal } from '../../..';
import { getFormattedPrice } from '../../utils';
import { PlanBenefits } from '../PlanBenefits';
import { planStyles } from '../planStyles';

const TeamsPlan: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { webPlans } = useSubscriptionContext();
    const isFreeTrialEligible = useTrialEligibility();

    const openDialog = () => {
        setIsModalOpen(true);
    };

    const buttonText = isFreeTrialEligible
        ? t('SelectSubscription:button:confirm:start_trial')
        : t('SelectSubscription:button:confirm:subscribe');

    return (
        <>
            {isModalOpen && (
                <UnlockPremiumModal
                    title={t('PremiumPage:promotionBanner:teams:title')}
                    description={t(
                        'PremiumPage:promotionBanner:teams:description',
                    )}
                    logo={
                        <Box className="flex gap-2 items-center">
                            <EquinetTeams
                                width={210}
                                color={ENTITY_COLOR.teams}
                            />
                        </Box>
                    }
                    color={ENTITY_COLOR.teams}
                    close={() => setIsModalOpen(false)}
                />
            )}

            <Box sx={planStyles.wrapper}>
                <Box
                    sx={{
                        ...planStyles.container,
                        background: `linear-gradient(180deg, ${COLOR.magnolia}, ${COLOR.prelude})`,
                    }}
                >
                    <Lottie
                        className="absolute right-0 bottom-0 left-0 opacity-30 -z-1"
                        animationData={purpleWave}
                    />
                    <Box
                        sx={{
                            ...planStyles.box,
                            backgroundColor: COLOR.white,
                            border: `1px solid ${ENTITY_COLOR.teams}`,
                            alignSelf: 'flex-start',
                        }}
                    >
                        <Typography color={ENTITY_COLOR.teams} fontSize={10}>
                            {t('PremiumPage:subscriptionPlans:teams:label')}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        <EquinetTeams color={ENTITY_COLOR.teams} />
                    </Box>

                    <Typography fontSize={12} color={COLOR.slateGray}>
                        {t('PremiumPage:subscriptionPlans:teams:description')}
                    </Typography>

                    <Box>
                        <Typography fontSize={24} fontWeight={700}>
                            {getFormattedPrice(webPlans.teams)}
                        </Typography>
                        <Typography fontSize={11} color={COLOR.slateGray}>
                            /
                            {t(
                                'PremiumPage:subscriptionPlans:teams:priceDescription',
                            )}
                        </Typography>
                    </Box>

                    <Button
                        sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: COLOR.white,
                            background: `linear-gradient(180deg, ${COLOR.eastSide}, ${ENTITY_COLOR.teams})`,
                            borderRadius: '50px',
                            borderColor: COLOR.linkWater,
                            mt: 'auto',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}
                        variant="outlined"
                        endIcon={<RightArrowIcon color={COLOR.white} />}
                        caption={buttonText}
                        onClick={openDialog}
                    />
                </Box>

                <PlanBenefits
                    benefits={TEAM_BENEFITS}
                    title={t('PremiumPage:subscriptionPlans:teams:benefits')}
                />
            </Box>
        </>
    );
};

export default TeamsPlan;
